/**
 * ScrollTextSection Component — Jackstraw Apartments
 *
 * Adapted from Seattle House's ScrollTextSection. Two-part scroll experience:
 *   PART A — "LIVE-X" text block with animated lead word + rotating word stack,
 *            location panel, and CTA. Scrolls normally (no pin).
 *   PART B — Amenity gallery with sticky scroll transition from State 1 (location
 *            images) to State 2 (amenity images) via per-column clip-path wipe.
 */

'use client';

import React, { useRef, useEffect, useState, useCallback, type MouseEvent } from 'react';
import {
  useContentToken,
  EditableToken,
  useSlotGroup,
  EditableSlotGroup,
  useSlotGroupOverrides,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ── Utility helpers (inlined — no external dependency) ───────────────────────

/** Clamp a value to [0, 1]. */
function clamp(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Map `v` from [inMin, inMax] → [outMin, outMax], clamped. */
function mapRange(v: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  const t = clamp((v - inMin) / (inMax - inMin));
  return outMin + t * (outMax - outMin);
}

const WORD_COUNT = 5;

// Scroll budget (vh) the amenity gallery stays pinned during transition.
const AMENITY_SCROLL_VH = 160;

// ── Slot definitions ─────────────────────────────────────────────────────────

const card1Definition: SlotGroupDefinition = {
  label: 'Location Image 1',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[0].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Mountain View' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', caption: 'title' } },
};
const card2Definition: SlotGroupDefinition = {
  label: 'Location Image 2',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[1].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Deschutes River' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', caption: 'title' } },
};
const amenity1Def: SlotGroupDefinition = {
  label: 'Amenity 1',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[2].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Fitness Center' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', caption: 'title' } },
};
const amenity2Def: SlotGroupDefinition = {
  label: 'Amenity 2',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[3].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Pool' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', caption: 'title' } },
};
const amenity3Def: SlotGroupDefinition = {
  label: 'Amenity 3',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[5].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Clubhouse' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', caption: 'title' } },
};
const amenity4Def: SlotGroupDefinition = {
  label: 'Amenity 4',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[6].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Patio' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 6, fieldMappings: { image: 'image', caption: 'title' } },
};
const amenity5Def: SlotGroupDefinition = {
  label: 'Amenity 5',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[7].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Lobby' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 7, fieldMappings: { image: 'image', caption: 'title' } },
};
const amenity6Def: SlotGroupDefinition = {
  label: 'Amenity 6',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[8].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Outdoor Space' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 8, fieldMappings: { image: 'image', caption: 'title' } },
};
const amenity7Def: SlotGroupDefinition = {
  label: 'Amenity 7',
  fields: {
    image:   { label: 'Image',   type: 'image', defaultField: 'photo_gallery[9].image', defaultValue: '' },
    caption: { label: 'Caption', type: 'text',  defaultValue: 'Dog Park' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 9, fieldMappings: { image: 'image', caption: 'title' } },
};

// ── Content schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'ScrollTextSection',
  displayName: 'Scroll Text Section',
  tokens: {
    'scrolltextsection.bg_image':      { label: 'Background Image',       type: 'image', defaultField: 'photo_gallery[4].image', defaultValue: '' },
    'scrolltextsection.lead_word':     { label: 'Lead Word',              type: 'text',  defaultValue: 'Live' },
    'scrolltextsection.word_1':        { label: 'Word 1',                 type: 'text',  defaultValue: 'In Bend.' },
    'scrolltextsection.word_2':        { label: 'Word 2',                 type: 'text',  defaultValue: 'Outdoors.' },
    'scrolltextsection.word_3':        { label: 'Word 3',                 type: 'text',  defaultValue: 'Effortlessly.' },
    'scrolltextsection.word_4':        { label: 'Word 4',                 type: 'text',  defaultValue: 'The Moment.' },
    'scrolltextsection.word_5':        { label: 'Word 5',                 type: 'text',  defaultField: 'property_name', defaultValue: 'Jackstraw Apartments.' },
    'scrolltextsection.location_tag':  { label: 'Location Tag',           type: 'text',  defaultValue: 'IN SOUTHERN CROSSING' },
    'scrolltextsection.location_desc': { label: 'Location Description',   type: 'text',  defaultValue: 'Located in Bend, Oregon, Jackstraw Apartments offer easy access to outdoor recreation and vibrant local culture.' },
    'scrolltextsection.cta_text':      { label: 'CTA Button Text',        type: 'text',  defaultValue: 'Apply Now' },
    'scrolltextsection.cta_url':       { label: 'CTA Button URL',         type: 'url',   defaultValue: '#contact' },
    'scrolltextsection.scroll_cue':    { label: 'Scroll Cue Caption',     type: 'text',  defaultValue: '[ Scroll to experience Jackstraw ]' },
    'scrolltextsection.gallery_title_1': { label: 'Gallery Title 1',      type: 'text',  defaultValue: 'Location Like No Other' },
    'scrolltextsection.gallery_title_2': { label: 'Gallery Title 2',      type: 'text',  defaultValue: 'Amenities Beyond Compare' },
  },
  slotGroups: {
    'scrolltextsection.card_1': card1Definition,
    'scrolltextsection.card_2': card2Definition,
    'scrolltextsection.amenity_1': amenity1Def,
    'scrolltextsection.amenity_2': amenity2Def,
    'scrolltextsection.amenity_3': amenity3Def,
    'scrolltextsection.amenity_4': amenity4Def,
    'scrolltextsection.amenity_5': amenity5Def,
    'scrolltextsection.amenity_6': amenity6Def,
    'scrolltextsection.amenity_7': amenity7Def,
  },
};

// ── Location panel ───────────────────────────────────────────────────────────

const locTagStyle:  React.CSSProperties = { fontFamily: 'var(--vibe-font-body)', fontSize: '1rem', fontWeight: 400, textTransform: 'uppercase', color: '#fff', margin: 0 };
const locDescStyle: React.CSSProperties = { fontFamily: 'var(--vibe-font-body)', fontSize: '1rem', fontWeight: 400, lineHeight: 1.6, color: '#fff', margin: 0, maxWidth: '32rem' };

const StaticLocationPanel: React.FC = () => {
  const tag  = useContentToken('scrolltextsection.location_tag',  { defaultValue: 'IN SOUTHERN CROSSING' });
  const desc = useContentToken('scrolltextsection.location_desc', { defaultValue: 'Located in Bend, Oregon, Jackstraw Apartments offer easy access to outdoor recreation and vibrant local culture.' });
  return (
    <div className="js-scrolltext-loc-panel" style={{ display: 'flex', gap: '1rem' }}>
      <div style={{ flex: 1 }}>
        <EditableToken path="scrolltextsection.location_tag" as="p" style={locTagStyle}>{tag || 'IN SOUTHERN CROSSING'}</EditableToken>
      </div>
      <div style={{ flex: 1 }}>
        <EditableToken path="scrolltextsection.location_desc" as="p" style={locDescStyle}>{desc || 'Located in Bend, Oregon, Jackstraw Apartments offer easy access to outdoor recreation and vibrant local culture.'}</EditableToken>
      </div>
    </div>
  );
};

// ── Card / heading styles ────────────────────────────────────────────────────

const imgCardSlotStyle:    React.CSSProperties = { position: 'relative', width: '100%', height: '100%', overflow: 'hidden' };
const imgCardImgStyle:     React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
const imgCardEmptyStyle:   React.CSSProperties = { width: '100%', height: '100%', backgroundColor: '#1a1a1a' };
const imgCardCaptionStyle: React.CSSProperties = { position: 'absolute', bottom: 0, right: 0, backgroundColor: '#101010', color: '#fff', fontFamily: 'var(--vibe-font-body)', fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '0.75rem 1.25rem', lineHeight: 1 };
const hdgCardStyle:        React.CSSProperties = { width: '100%', height: '100%', backgroundColor: '#101010', display: 'flex', alignItems: 'center', padding: '2rem 2.5rem' };
const hdgTextStyle:        React.CSSProperties = { fontFamily: 'var(--vibe-font-heading)', fontSize: 'clamp(32px, 4.5vw, 64px)', fontWeight: 500, lineHeight: 0.95, letterSpacing: '-0.02em', textTransform: 'uppercase', color: '#fff', margin: 0 };

const scrollCueStyle: React.CSSProperties = {
  fontFamily: 'var(--vibe-font-body)', fontSize: 'clamp(0.8rem, 1.4vw, 1rem)',
  letterSpacing: '0.08em', color: 'rgba(255,255,255,0.72)', textAlign: 'center', margin: 0,
};

// ── Animation state ──────────────────────────────────────────────────────────

interface Anim {
  liveY:         number;
  activeIndex:   number;
  col1Reveal:    number;
  col2Reveal:    number;
  col3Reveal:    number;
  col4Reveal:    number;
}
const INIT: Anim = { liveY: 0, activeIndex: 0, col1Reveal: 0, col2Reveal: 0, col3Reveal: 0, col4Reveal: 0 };

// ── Component ────────────────────────────────────────────────────────────────

const Component: React.FC = () => {
  const liveRowRef = useRef<HTMLDivElement>(null);
  const galleryScrollRef = useRef<HTMLDivElement>(null);
  const wordRefs    = useRef<(HTMLDivElement | null)[]>([]);
  const setWordRef  = useCallback((i: number) => (el: HTMLDivElement | null) => { wordRefs.current[i] = el; }, []);
  const offsetsRef  = useRef<number[]>([]);
  const rafRef      = useRef<number>(0);

  const [travelPx, setTravelPx] = useState(268);
  const [anim, setAnim]         = useState<Anim>(INIT);

  // ── Content tokens ─────────────────────────────────────────────────────────

  const bgImage   = useContentToken('scrolltextsection.bg_image',  { defaultField: 'photo_gallery[4].image', defaultValue: '' });
  const leadWord  = useContentToken('scrolltextsection.lead_word', { defaultValue: 'Live' });
  const word0     = useContentToken('scrolltextsection.word_1', { defaultValue: 'In Bend.' });
  const word1     = useContentToken('scrolltextsection.word_2', { defaultValue: 'Outdoors.' });
  const word2     = useContentToken('scrolltextsection.word_3', { defaultValue: 'Effortlessly.' });
  const word3     = useContentToken('scrolltextsection.word_4', { defaultValue: 'The Moment.' });
  const word4     = useContentToken('scrolltextsection.word_5', { defaultField: 'property_name', defaultValue: 'Jackstraw Apartments.' });
  const words     = [word0, word1, word2, word3, word4];

  const ctaText      = useContentToken('scrolltextsection.cta_text', { defaultValue: 'Apply Now' }) || 'Apply Now';
  const ctaUrl       = useContentToken('scrolltextsection.cta_url',  { defaultValue: '#contact' })  || '#contact';
  const scrollCue    = useContentToken('scrolltextsection.scroll_cue', { defaultValue: '[ Scroll to experience Jackstraw ]' });
  const galleryTitle1 = useContentToken('scrolltextsection.gallery_title_1', { defaultValue: 'Location Like No Other' });
  const galleryTitle2 = useContentToken('scrolltextsection.gallery_title_2', { defaultValue: 'Amenities Beyond Compare' });
  const card1    = useSlotGroup('scrolltextsection.card_1', card1Definition);
  const card2    = useSlotGroup('scrolltextsection.card_2', card2Definition);
  const am1      = useSlotGroup('scrolltextsection.amenity_1', amenity1Def);
  const am2      = useSlotGroup('scrolltextsection.amenity_2', amenity2Def);
  const am3      = useSlotGroup('scrolltextsection.amenity_3', amenity3Def);
  const am4      = useSlotGroup('scrolltextsection.amenity_4', amenity4Def);
  const am5      = useSlotGroup('scrolltextsection.amenity_5', amenity5Def);
  const am6      = useSlotGroup('scrolltextsection.amenity_6', amenity6Def);
  const am7      = useSlotGroup('scrolltextsection.amenity_7', amenity7Def);
  const overrides = useSlotGroupOverrides();

  const handleCtaClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('contact');
    if (!el) return;
    e.preventDefault();
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
  }, []);

  // ── Measure word travel ────────────────────────────────────────────────────

  useEffect(() => {
    const measure = () => {
      const els = wordRefs.current;
      if (els.length < WORD_COUNT || els.some((e) => !e)) return;
      const baseRect   = els[0]!.getBoundingClientRect();
      const baseCenter = baseRect.top + baseRect.height / 2;
      const offsets = els.map((e) => {
        const r = e!.getBoundingClientRect();
        return (r.top + r.height / 2) - baseCenter;
      });
      offsetsRef.current = offsets;
      const span = offsets[WORD_COUNT - 1];
      if (span > 0) setTravelPx(span);
    };
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
    };
  }, [word0, word1, word2, word3, word4]);

  // ── Scroll animation ─────────────────────────────────────────────────────

  useEffect(() => {
    const update = () => {
      const vh = window.innerHeight;

      // LIVE word animation (PART A)
      let liveY = 0;
      let activeIndex = 0;
      if (liveRowRef.current && travelPx > 0) {
        const r = liveRowRef.current.getBoundingClientRect();
        const liveProgress = clamp((vh * 0.55 - r.top) / (vh * 0.40));
        liveY = liveProgress * travelPx;
        const offsets = offsetsRef.current;
        if (offsets.length === WORD_COUNT) {
          let best = 0;
          let bestDist = Infinity;
          for (let i = 0; i < WORD_COUNT; i++) {
            const dist = Math.abs(liveY - offsets[i]);
            if (dist < bestDist) { bestDist = dist; best = i; }
          }
          activeIndex = best;
        } else {
          activeIndex = Math.min(Math.floor(liveProgress * WORD_COUNT), WORD_COUNT - 1);
        }
      }

      // Gallery State 1 → State 2 transition (PART B)
      let clipP = 0;
      if (galleryScrollRef.current) {
        const gr = galleryScrollRef.current.getBoundingClientRect();
        const budget = vh * (AMENITY_SCROLL_VH / 100);
        clipP = budget > 0 ? clamp(-gr.top / budget) : 0;
      }
      const col1Reveal = mapRange(clipP, 0.00, 0.35, 0, 1);
      const col2Reveal = mapRange(clipP, 0.15, 0.50, 0, 1);
      const col3Reveal = mapRange(clipP, 0.30, 0.65, 0, 1);
      const col4Reveal = mapRange(clipP, 0.72, 1.00, 0, 1);

      setAnim({ liveY, activeIndex, col1Reveal, col2Reveal, col3Reveal, col4Reveal });
    };

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [travelPx]);

  // ── Styles ─────────────────────────────────────────────────────────────────

  const headingStyle: React.CSSProperties = {
    fontFamily: 'var(--vibe-font-heading, sans-serif)',
    fontSize: 'clamp(1.55rem, 3.5vw, 3.5rem)',
    fontWeight: 500, textTransform: 'uppercase', color: '#fff', lineHeight: 1.2, margin: 0,
  };
  const colStyle: React.CSSProperties = { flex: 1, position: 'relative', overflow: 'hidden' };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div data-vibesite-slot="ScrollTextSection" style={{ backgroundColor: '#000', position: 'relative', overflow: 'clip' }}>

      {/* ═══════════════════════════════════════════════════════════════════════
          PART A — LIVE-X text block. Normal flow (NOT sticky).
         ═══════════════════════════════════════════════════════════════════════ */}
      <div className="js-text-block" style={{ position: 'relative', overflow: 'hidden' }}>

        {/* Background */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          <EditableToken path="scrolltextsection.bg_image" type="image" as="div">
            {bgImage ? (
              <VibesitesImage url={bgImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : null}
          </EditableToken>
        </div>
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, backgroundColor: 'rgba(0,0,0,0.55)' }} />
        {/* Top fade-from-black gradient overlay */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '40%', zIndex: 1, background: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)', pointerEvents: 'none' }} />
        {/* Bottom fade-to-black gradient overlay */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', zIndex: 1, background: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)', pointerEvents: 'none' }} />

        {/* Content */}
        <div className="js-text-area" style={{
          position: 'relative', zIndex: 2,
          display: 'flex', flexDirection: 'column', gap: 'clamp(1.5rem, 4vh, 4rem)',
          padding: '160px clamp(1.5rem, 5vw, 5rem)',
        }}>

          {/* LIVE + words */}
          <div ref={liveRowRef} className="js-scrolltext-top-row" style={{ display: 'flex', gap: '1.5rem', position: 'relative', overflow: 'visible' }}>

            {/* Left: LIVE — absolute, scroll-driven translateY */}
            <div className="js-scrolltext-lead-col" style={{ flex: 1, position: 'relative', minHeight: 0 }}>
              <div style={{ position: 'absolute', right: 0, top: 0, transform: `translateY(${anim.liveY}px)`, willChange: 'transform' }}>
                <EditableToken path="scrolltextsection.lead_word" as="div" style={headingStyle}>
                  {leadWord || 'Live'}
                </EditableToken>
              </div>
            </div>

            {/* Right: word stack */}
            <div className="js-scrolltext-word-stack" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'clamp(0.4rem, 1.4vh, 1rem)' }}>
              <div ref={setWordRef(0)}>
                <EditableToken path="scrolltextsection.word_1" as="div" style={{ ...headingStyle, opacity: anim.activeIndex === 0 ? 1 : 0.25, transition: 'opacity 0.2s ease' }}>
                  {words[0] || 'In Bend.'}
                </EditableToken>
              </div>
              <div ref={setWordRef(1)}>
                <EditableToken path="scrolltextsection.word_2" as="div" style={{ ...headingStyle, opacity: anim.activeIndex === 1 ? 1 : 0.25, transition: 'opacity 0.2s ease' }}>
                  {words[1] || 'Outdoors.'}
                </EditableToken>
              </div>
              <div ref={setWordRef(2)}>
                <EditableToken path="scrolltextsection.word_3" as="div" style={{ ...headingStyle, opacity: anim.activeIndex === 2 ? 1 : 0.25, transition: 'opacity 0.2s ease' }}>
                  {words[2] || 'Effortlessly.'}
                </EditableToken>
              </div>
              <div ref={setWordRef(3)}>
                <EditableToken path="scrolltextsection.word_4" as="div" style={{ ...headingStyle, opacity: anim.activeIndex === 3 ? 1 : 0.25, transition: 'opacity 0.2s ease' }}>
                  {words[3] || 'The Moment.'}
                </EditableToken>
              </div>
              <div ref={setWordRef(4)}>
                <EditableToken path="scrolltextsection.word_5" as="div" defaultField="property_name" style={{ ...headingStyle, opacity: anim.activeIndex === 4 ? 1 : 0.25, transition: 'opacity 0.2s ease' }}>
                  {words[4] || 'Jackstraw Apartments.'}
                </EditableToken>
              </div>
            </div>
          </div>

          {/* Location + CTA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <StaticLocationPanel />
            <div className="js-scrolltext-cta-row" style={{ display: 'flex', gap: '1rem' }}>
              <div className="js-scrolltext-cta-spacer" style={{ flex: 1 }} />
              <div className="js-scrolltext-cta-col" style={{ flex: 1 }}>
                <EditableToken path="scrolltextsection.cta_url" type="url">
                  <a href={ctaUrl} onClick={handleCtaClick} className="js-scrolltext-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', fontFamily: 'var(--vibe-font-heading, sans-serif)', fontSize: '16px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', textDecoration: 'none', color: '#000', border: 'none', backgroundColor: '#fff', whiteSpace: 'nowrap', transition: 'opacity 0.25s ease, transform 0.15s ease' }}>
                    <EditableToken path="scrolltextsection.cta_text" as="span">{ctaText}</EditableToken>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </a>
                </EditableToken>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          PART B — amenity gallery. Sticky scroll transition.
         ═══════════════════════════════════════════════════════════════════════ */}
      <div ref={galleryScrollRef} className="js-amenity-scroll" style={{ position: 'relative', height: `calc(100vh + ${AMENITY_SCROLL_VH}vh)`, width: '100vw', marginLeft: 'calc(50% - 50vw)' }}>
        <div
          className="js-amenity-sticky"
          style={{
            position: 'sticky', top: 0, height: '100vh', overflow: 'hidden', backgroundColor: '#000',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
            gap: 'clamp(1rem, 3vh, 2.25rem)', padding: 0,
          }}
        >
          <div className="js-gallery-grid" style={{ position: 'relative', width: '100%', height: 'min(calc(100vh - 6rem), 640px)' }}>

            {/* ── Desktop gallery (hidden ≤767px): 3-column, 2-state ─────── */}
            <div className="js-gallery-desktop" style={{ width: '100%', height: '100%', position: 'relative' }}>

              {/* State 1 — Location (base) */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 1 }}>
                <div style={colStyle}>
                  <EditableSlotGroup groupPath="scrolltextsection.card_1" definition={card1Definition} override={overrides['scrolltextsection.card_1']} style={imgCardSlotStyle}>
                    {card1.image ? <VibesitesImage url={card1.image} alt={card1.caption || 'Mountain View'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{card1.caption || 'Mountain View'}</span>
                  </EditableSlotGroup>
                </div>
                <div style={colStyle}>
                  <EditableSlotGroup groupPath="scrolltextsection.card_2" definition={card2Definition} override={overrides['scrolltextsection.card_2']} style={imgCardSlotStyle}>
                    {card2.image ? <VibesitesImage url={card2.image} alt={card2.caption || 'Deschutes River'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{card2.caption || 'Deschutes River'}</span>
                  </EditableSlotGroup>
                </div>
                <div style={colStyle}>
                  <div style={hdgCardStyle}>
                    <EditableToken path="scrolltextsection.gallery_title_1" as="h2" style={hdgTextStyle}>{galleryTitle1 || 'Location Like No Other'}</EditableToken>
                  </div>
                </div>
              </div>

              {/* State 2 — Amenities (wipes down over State 1) */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 2, pointerEvents: anim.col3Reveal >= 1 ? 'auto' : 'none' }}>
                <div style={{ ...colStyle, clipPath: `inset(0 0 ${(1 - anim.col1Reveal) * 100}% 0)`, willChange: 'clip-path' }}>
                  <div style={hdgCardStyle}>
                    <EditableToken path="scrolltextsection.gallery_title_2" as="h2" style={hdgTextStyle}>{galleryTitle2 || 'Amenities Beyond Compare'}</EditableToken>
                  </div>
                </div>
                <div style={{ ...colStyle, clipPath: `inset(0 0 ${(1 - anim.col2Reveal) * 100}% 0)`, willChange: 'clip-path' }}>
                  <EditableSlotGroup groupPath="scrolltextsection.amenity_1" definition={amenity1Def} override={overrides['scrolltextsection.amenity_1']} style={imgCardSlotStyle}>
                    {am1.image ? <VibesitesImage url={am1.image} alt={am1.caption || 'Fitness Center'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{am1.caption || 'Fitness Center'}</span>
                  </EditableSlotGroup>
                </div>
                <div style={{ ...colStyle, clipPath: `inset(0 0 ${(1 - anim.col3Reveal) * 100}% 0)`, willChange: 'clip-path' }}>
                  <EditableSlotGroup groupPath="scrolltextsection.amenity_2" definition={amenity2Def} override={overrides['scrolltextsection.amenity_2']} style={imgCardSlotStyle}>
                    {am2.image ? <VibesitesImage url={am2.image} alt={am2.caption || 'Pool'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{am2.caption || 'Pool'}</span>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>

            {/* ── Mobile gallery (hidden >767px): 2×2, amenities only ────── */}
            <div className="js-gallery-mobile" style={{ display: 'none', width: '100%', height: '100%', position: 'relative' }}>

              {/* Layer 1 — base */}
              <div className="js-mob-layer" style={{ position: 'absolute', inset: 0, display: 'flex', flexWrap: 'wrap', zIndex: 1 }}>
                <div className="js-mob-cell" style={colStyle}>
                  <EditableSlotGroup groupPath="scrolltextsection.amenity_1" definition={amenity1Def} override={overrides['scrolltextsection.amenity_1']} style={imgCardSlotStyle}>
                    {am1.image ? <VibesitesImage url={am1.image} alt={am1.caption || 'Fitness Center'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{am1.caption || 'Fitness Center'}</span>
                  </EditableSlotGroup>
                </div>
                <div className="js-mob-cell" style={colStyle}>
                  <EditableSlotGroup groupPath="scrolltextsection.amenity_2" definition={amenity2Def} override={overrides['scrolltextsection.amenity_2']} style={imgCardSlotStyle}>
                    {am2.image ? <VibesitesImage url={am2.image} alt={am2.caption || 'Pool'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{am2.caption || 'Pool'}</span>
                  </EditableSlotGroup>
                </div>
                <div className="js-mob-cell js-mob-heading" style={colStyle}>
                  <div style={hdgCardStyle}>
                    <EditableToken path="scrolltextsection.gallery_title_2" as="h2" style={hdgTextStyle}>{galleryTitle2 || 'Amenities Beyond Compare'}</EditableToken>
                  </div>
                </div>
                <div className="js-mob-cell" style={colStyle}>
                  <EditableSlotGroup groupPath="scrolltextsection.amenity_3" definition={amenity3Def} override={overrides['scrolltextsection.amenity_3']} style={imgCardSlotStyle}>
                    {am3.image ? <VibesitesImage url={am3.image} alt={am3.caption || 'Clubhouse'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{am3.caption || 'Clubhouse'}</span>
                  </EditableSlotGroup>
                </div>
              </div>

              {/* Layer 2 — wipe */}
              <div className="js-mob-layer" style={{ position: 'absolute', inset: 0, display: 'flex', flexWrap: 'wrap', zIndex: 2, pointerEvents: anim.col3Reveal >= 1 ? 'auto' : 'none' }}>
                <div className="js-mob-cell" style={{ ...colStyle, clipPath: `inset(0 0 ${(1 - anim.col1Reveal) * 100}% 0)`, willChange: 'clip-path' }}>
                  <EditableSlotGroup groupPath="scrolltextsection.amenity_4" definition={amenity4Def} override={overrides['scrolltextsection.amenity_4']} style={imgCardSlotStyle}>
                    {am4.image ? <VibesitesImage url={am4.image} alt={am4.caption || 'Patio'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{am4.caption || 'Patio'}</span>
                  </EditableSlotGroup>
                </div>
                <div className="js-mob-cell" style={{ ...colStyle, clipPath: `inset(0 0 ${(1 - anim.col2Reveal) * 100}% 0)`, willChange: 'clip-path' }}>
                  <EditableSlotGroup groupPath="scrolltextsection.amenity_5" definition={amenity5Def} override={overrides['scrolltextsection.amenity_5']} style={imgCardSlotStyle}>
                    {am5.image ? <VibesitesImage url={am5.image} alt={am5.caption || 'Lobby'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{am5.caption || 'Lobby'}</span>
                  </EditableSlotGroup>
                </div>
                {/* Empty cell — heading from Layer 1 shows through */}
                <div className="js-mob-cell js-mob-heading" style={colStyle} />
                <div className="js-mob-cell" style={{ ...colStyle, clipPath: `inset(0 0 ${(1 - anim.col3Reveal) * 100}% 0)`, willChange: 'clip-path' }}>
                  <EditableSlotGroup groupPath="scrolltextsection.amenity_6" definition={amenity6Def} override={overrides['scrolltextsection.amenity_6']} style={imgCardSlotStyle}>
                    {am6.image ? <VibesitesImage url={am6.image} alt={am6.caption || 'Outdoor Space'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                    <span style={imgCardCaptionStyle}>{am6.caption || 'Outdoor Space'}</span>
                  </EditableSlotGroup>
                </div>
              </div>

              {/* Layer 3 — heading cover */}
              <div style={{ position: 'absolute', left: 0, bottom: 0, width: '50%', height: '50%', zIndex: 3, clipPath: `inset(0 0 ${(1 - anim.col4Reveal) * 100}% 0)`, willChange: 'clip-path', pointerEvents: anim.col4Reveal >= 1 ? 'auto' : 'none' }}>
                <EditableSlotGroup groupPath="scrolltextsection.amenity_7" definition={amenity7Def} override={overrides['scrolltextsection.amenity_7']} style={imgCardSlotStyle}>
                  {am7.image ? <VibesitesImage url={am7.image} alt={am7.caption || 'Dog Park'} style={imgCardImgStyle} /> : <div style={imgCardEmptyStyle} />}
                  <span style={imgCardCaptionStyle}>{am7.caption || 'Dog Park'}</span>
                </EditableSlotGroup>
              </div>
            </div>

          </div>

          {/* Scroll cue caption */}
          <EditableToken path="scrolltextsection.scroll_cue" as="p" style={scrollCueStyle}>
            {scrollCue || '[ Scroll to experience Jackstraw ]'}
          </EditableToken>

        </div>
      </div>

      <style>{`
        .js-scrolltext-cta:hover         { opacity: 0.85; }
        .js-scrolltext-cta:active        { transform: scale(0.98); }
        .js-scrolltext-cta:focus-visible { outline: 2px solid rgba(255,255,255,0.7); outline-offset: 2px; }

        @media screen and (max-width: 991px) {
          .js-text-block .js-text-area { padding-top: 96px !important; padding-bottom: 96px !important; }
          .js-scrolltext-lead-col > div > div,
          .js-scrolltext-top-row > div:last-child > div > div {
            font-size: 2.5rem !important; line-height: 1.2 !important;
          }
          .js-scrolltext-loc-panel { gap: 0.75rem !important; }
          .js-scrolltext-loc-panel > div p { font-size: 0.875rem !important; }
        }

        @media screen and (max-width: 767px) {
          .js-text-block .js-text-area { padding-top: 72px !important; padding-bottom: 72px !important; }
          .js-scrolltext-loc-panel { flex-direction: column !important; gap: 0.5rem !important; }
          .js-scrolltext-loc-panel > div p { font-size: 0.8125rem !important; line-height: 1.55 !important; }
          .js-scrolltext-cta-spacer { display: none !important; }
          .js-scrolltext-cta-col    { flex: 1 1 100% !important; width: 100% !important; }
          .js-scrolltext-cta-col > * { display: block !important; width: 100% !important; }
          .js-scrolltext-cta { display: flex !important; width: 100% !important; justify-content: center !important; padding: 14px 18px !important; font-size: 14px !important; white-space: normal !important; }
          .js-gallery-desktop { display: none !important; }
          .js-gallery-mobile  { display: block !important; }
          .js-gallery-grid {
            width: min(94vw, 560px) !important;
            height: clamp(480px, 78vh, 760px) !important;
          }
          .js-mob-cell {
            flex: 0 0 50% !important;
            width: 50% !important;
            height: 50% !important;
          }
          .js-mob-heading > div { padding: 1.5rem !important; }
          .js-mob-heading h2 { font-size: clamp(20px, 5vw, 32px) !important; }
        }

        @media screen and (min-width: 480px) and (max-width: 767px) {
          .js-scrolltext-top-row { gap: 0.5rem !important; }
          .js-scrolltext-lead-col > div > div,
          .js-scrolltext-top-row > div:last-child > div > div {
            font-size: 1.85rem !important; line-height: 1.2 !important;
          }
        }

        @media screen and (max-width: 479px) {
          .js-text-block .js-text-area { padding-top: 56px !important; padding-bottom: 56px !important; }
          .js-scrolltext-top-row  { gap: 0.5rem !important; }
          .js-scrolltext-lead-col > div > div,
          .js-scrolltext-top-row > div:last-child > div > div {
            font-size: 1.55rem !important; line-height: 1.25 !important;
          }
          .js-scrolltext-lead-col {
            flex: 0 0 30% !important;
          }
        }
      `}</style>
    </div>
  );
};

// ── Export ────────────────────────────────────────────────────────────────────

interface ScrollTextSectionWithSchema extends React.FC {
  contentSchema: typeof contentSchema;
}

export const ScrollTextSection = Component as ScrollTextSectionWithSchema;
(ScrollTextSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScrollTextSection;
